/**
 * ModernSidebar - Sidebar Component for Modern Layout
 * 
 * Uses the shared UI component library Sidebar.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import get from 'lodash/get';
import { notification } from 'antd';
import { Sidebar } from '@crc-evans/ui-components';
import { FolderOpen, Briefcase, Wrench, ClipboardCheck, Headphones, LogOut } from 'lucide-react';
import { signOut } from '../../redux/actions/authActions';
import './ModernSidebar.css';

/** Stable fallback so connect(mapState) never returns a new [] each render (avoids update loops). */
const EMPTY_USER_ACCESS_LIST = [];

const readUserGroupsFromStorage = () => {
  try {
    const raw = localStorage.getItem('UserGroup');
    if (raw == null || raw === '') return EMPTY_USER_ACCESS_LIST;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : EMPTY_USER_ACCESS_LIST;
  } catch {
    return EMPTY_USER_ACCESS_LIST;
  }
};

const ModernSidebar = (props) => {
  const history = useHistory();
  const location = useLocation();
  const { userAccessList, onSignout } = props;
  // Technical routes: highlight Technical when on any of these
  const technicalPaths = ['/heat-profile', '/DCALGenerator', '/TechnicalPage', '/technical'];
  const calibrationPaths = ['/calibration'];

  // Determine initial active state based on current path
  const [activeId, setActiveId] = useState(() => {
    const path = window.location.pathname;
    if (technicalPaths.some((p) => path === p || path.startsWith(p + '/'))) return 'technical';
    if (calibrationPaths.some((p) => path === p || path.startsWith(p + '/'))) return 'calibration';
    if (
      path === '/projects' ||
      path.startsWith('/projects/') ||
      path === '/dashboard' ||
      path.startsWith('/dashboard/') ||
      path === '/dpr' ||
      path.startsWith('/dpr')
    ) {
      return 'projects';
    }
    if (path === '/admin' || path.startsWith('/admin/')) return 'admin';
    return 'projects';
  });

  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  /** Prefer Redux when set; otherwise localStorage UserGroup (refresh and Hub race). */
  const userGroup = useMemo(() => {
    const fromRedux = Array.isArray(userAccessList) ? userAccessList : [];
    if (fromRedux.length > 0) return fromRedux;
    return readUserGroupsFromStorage();
  }, [userAccessList]);

  /* Groups: hydrated in routes (AppContainer). Do not dispatch setUserAccessList here (Redux update loops). */

  // Map user data for Sidebar component
  const defaultUser = {
    name: user['custom:first_name'] && user['custom:last_name']
      ? `${user['custom:first_name']} ${user['custom:last_name']}`
      : user['custom:first_name'] || 'User',
    role: (userGroup?.includes('super-admin') || userGroup?.includes('admin')) ? 'Administrator' : 'User',
    initials: (
      (user['custom:first_name']?.charAt(0) || '') +
      (user['custom:last_name']?.charAt(0) || '')
    ).toUpperCase() || 'U'
  };

  // Update active state based on current route
  useEffect(() => {
    const path = location.pathname;
    if (technicalPaths.some((p) => path === p || path.startsWith(p + '/'))) {
      setActiveId('technical');
    } else if (calibrationPaths.some((p) => path === p || path.startsWith(p + '/'))) {
      setActiveId('calibration');
    } else if (
      path === '/projects' ||
      path.startsWith('/projects/') ||
      path === '/dashboard' ||
      path.startsWith('/dashboard/') ||
      path === '/dpr' ||
      path.startsWith('/dpr')
    ) {
      setActiveId('projects');
    } else if (path === '/admin' || path.startsWith('/admin/')) {
      setActiveId('admin');
    } else if (path === '/AskEvans') {
      setActiveId('askevans');
    } else {
      setActiveId('projects');
    }
  }, [location.pathname]);

  const handleItemClick = (itemId) => {
    if (itemId === 'projects') {
      setActiveId('projects');
      history.push('/projects');
    } else if (itemId === 'technical') {
      setActiveId('technical');
      history.push('/heat-profile');
    } else if (itemId === 'calibration') {
      setActiveId('calibration');
      history.push('/calibration');
    } else if (itemId === 'askevans') {
      setActiveId('askevans');
      history.push('/AskEvans');
    } else if (itemId === 'admin') {
      setActiveId('admin');
      history.push('/admin/asset-management');
    }
  };

  const items = [
    {
      id: 'projects',
      label: 'Projects',
      icon: FolderOpen,
    },
  ];

  if (
    userGroup?.includes('heat-profile') ||
    userGroup?.includes('coating-record-db') ||
    userGroup?.includes('dcal-generator') ||
    userGroup?.includes('super-admin') ||
    userGroup?.includes('admin')
  ) {
    items.push({
      id: 'technical',
      label: 'Technical',
      icon: Wrench,
    });
  }

  if (
    userGroup?.includes('heat-profile') ||
    userGroup?.includes('coating-record-db') ||
    userGroup?.includes('dcal-generator') ||
    userGroup?.includes('super-admin') ||
    userGroup?.includes('admin')
  ) {
    items.push({
      id: 'calibration',
      label: 'Calibration & Control',
      icon: ClipboardCheck,
    });
  }

 

  if (userGroup?.includes('admin') || userGroup?.includes('super-admin')) {
    items.push({
      id: 'admin',
      label: 'Admin',
      icon: Briefcase,
    });
  }

  if (
    userGroup?.includes('askevans-normal-user') ||
    userGroup?.includes('super-admin') ||
    userGroup?.includes('admin')
  ) {
    items.push({
      id: 'askevans',
      label: 'Ask Evans',
      icon: Headphones,
    });
  }

  const handleLogout = () => {
    onSignout();
    notification.destroy();
    history.push('/login');
    localStorage.clear();
    sessionStorage.clear();
    notification.success({
      duration: 3,
      message: 'Successfully Logged Out',
      description: '',
    });
  };

  return (
    <div className="modern-sidebar-shell">
      <div className="modern-sidebar-shell__main">
        <Sidebar
          items={items}
          activeItemId={activeId}
          onItemClick={handleItemClick}
          user={defaultUser}
          className="modern-sidebar-override"
        />
        <button
          type="button"
          className="modern-sidebar-logout-btn"
          onClick={handleLogout}
          aria-label="Logout"
        >
          <LogOut size={16} strokeWidth={2} aria-hidden />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
};

const mapStateToProps = ({ auth, equip }) => {
  const list = get(equip, 'userAccessList');
  return {
    userAccess: auth?.userAccess || [],
    userAccessList: Array.isArray(list) ? list : EMPTY_USER_ACCESS_LIST,
  };
};

const mapDispatchToProps = {
  onSignout: signOut,
};

export default connect(mapStateToProps, mapDispatchToProps)(ModernSidebar);
